import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  getOutlets,
  createReport,
  getReports,
  deleteReport,
  exportReportPdf,
  exportAllReportPdf,
} from "../apinew";
import { Download, Trash2, TrendingUp } from "lucide-react";

export default function LaporanPage() {
  const [outlets, setOutlets] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({
    outlet_id: "",
    period: "daily",
    start_date: "",
    end_date: "",
  });

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadOutlets();
    loadReports();
  }, []);

  const loadOutlets = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await getOutlets();
      const list = Array.isArray(res) ? res : res?.data ?? [];
      setOutlets(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Gagal load outlet:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Gagal memuat daftar outlet"
      );
      setOutlets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await getReports();
      const list = res?.data || res || [];
      setReports(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Gagal load reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!form.outlet_id) return alert("Pilih outlet dahulu!");
    if (!form.start_date || !form.end_date) {
      return alert("Isi tanggal mulai dan selesai!");
    }

    const payload = {
      outlet_id: form.outlet_id,
      period: form.period,
      start_date: form.start_date,
      end_date: form.end_date,
    };

    try {
      setGenerating(true);
      setError("");
      setSuccess("");

      await createReport(payload);

      setSuccess("Laporan berhasil dibuat!");
      setForm({ outlet_id: "", period: "daily", start_date: "", end_date: "" });
      await loadReports();
    } catch (err) {
      console.error("Gagal generate laporan:", err);
      setError(
        err.response?.data?.message || err.message || "Gagal membuat laporan"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Yakin hapus laporan ini?")) return;

    try {
      await deleteReport(id);
      setSuccess("Laporan berhasil dihapus!");
      await loadReports();
    } catch (err) {
      console.error("Gagal hapus laporan:", err);
      alert(
        err.response?.data?.message || err.message || "Gagal menghapus laporan"
      );
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const report = reports.find((r) => r.report_id === reportId);
      const outletName = report?.outlet?.name
        ? report.outlet.name
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "")
        : "laporan";

      const startDate = new Date(report?.start_date).toLocaleDateString(
        "en-CA"
      ); // YYYY-MM-DD format
      const endDate = new Date(report?.end_date).toLocaleDateString("en-CA");

      const filename = `Financial_Report_${outletName}_${startDate}_to_${endDate}.pdf`;

      const blob = await exportReportPdf(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Gagal download PDF:", err);
      alert("Gagal mengunduh laporan");
    }
  };

  const handleDownloadAllPdf = async () => {
    try {
      const timestamp = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD format
      const filename = `All_Financial_Reports_${timestamp}.pdf`;

      const blob = await exportAllReportPdf();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Gagal download semua PDF:", err);
      alert("Gagal mengunduh laporan");
    }
  };

  const getPeriodLabel = (p) => {
    if (p === "daily") return "Harian";
    if (p === "weekly") return "Mingguan";
    if (p === "monthly") return "Bulanan";
    return p;
  };

  const getPeriodColor = (p) => {
    if (p === "daily") return { bg: "#dbeafe", text: "#1e40af" };
    if (p === "weekly") return { bg: "#fef3c7", text: "#92400e" };
    if (p === "monthly") return { bg: "#f3e8ff", text: "#6b21a8" };
    return { bg: "#f1f5f9", text: "#64748b" };
  };

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "24px" }}
      >
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#0f172a",
            marginBottom: "4px",
          }}
        >
          Laporan
        </h2>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Buat dan kelola laporan keuangan
        </p>
      </motion.div>

      {(error || success) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: error ? "#fef2f2" : "#f0fdf4",
            border: `1px solid ${error ? "#fee2e2" : "#bbf7d0"}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: error ? "#991b1b" : "#166534",
            fontSize: "14px",
          }}
        >
          {error || success}
        </motion.div>
      )}

      {/* Generator Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          padding: "24px",
          marginBottom: "24px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#0f172a",
            marginBottom: "20px",
          }}
        >
          Buat Laporan Baru
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Outlet
            </label>
            <select
              value={form.outlet_id}
              onChange={(e) => setForm({ ...form, outlet_id: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            >
              <option value="">Pilih Outlet</option>
              {outlets.map((o) => (
                <option key={o.outlet_id} value={o.outlet_id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Periode
            </label>
            <select
              value={form.period}
              onChange={(e) => setForm({ ...form, period: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            >
              <option value="daily">Harian</option>
              <option value="weekly">Mingguan</option>
              <option value="monthly">Bulanan</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Tanggal Mulai
            </label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Tanggal Selesai
            </label>
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
          </div>
        </div>

        <div
          style={{
            marginTop: "20px",
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handleGenerate}
            disabled={generating}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
              e.currentTarget.style.boxShadow =
                "0 2px 6px rgba(59,130,246,0.3)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = generating ? 0.7 : 1;
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "6px",
              background: "#3b82f6",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: generating ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: generating ? 0.7 : 1,
              transition: "all 0.2s ease",
            }}
          >
            <TrendingUp size={16} />
            {generating ? "Membuat..." : "Buat Laporan"}
          </button>
        </div>
      </motion.div>

      {/* Reports List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#0f172a",
              margin: 0,
            }}
          >
            Riwayat Laporan
          </h3>
          {reports.length > 0 && (
            <button
              onClick={handleDownloadAllPdf}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
              style={{
                padding: "6px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                background: "#ffffff",
                color: "#64748b",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
              }}
            >
              <Download size={14} />
              Unduh Semua
            </button>
          )}
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Memuat...
            </div>
          ) : reports.length === 0 ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Belum ada laporan
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Outlet
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Periode
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Rentang Tanggal
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const periodColor = getPeriodColor(r.period);
                  return (
                    <tr
                      key={r.report_id}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f8fafc";
                        e.currentTarget.style.boxShadow =
                          "inset 0 -1px 0 #e2e8f0";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <td
                        style={{
                          padding: "16px 24px",
                          fontSize: "14px",
                          color: "#0f172a",
                          fontWeight: "500",
                        }}
                      >
                        {r.outlet?.name || "-"}
                      </td>
                      <td style={{ padding: "16px 24px" }}>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            background: periodColor.bg,
                            color: periodColor.text,
                          }}
                        >
                          {getPeriodLabel(r.period)}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "16px 24px",
                          fontSize: "14px",
                          color: "#64748b",
                        }}
                      >
                        {new Date(r.start_date).toLocaleDateString("id-ID")} -{" "}
                        {new Date(r.end_date).toLocaleDateString("id-ID")}
                      </td>
                      <td style={{ padding: "16px 24px", textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handleDownloadPdf(r.report_id)}
                            style={{
                              padding: "6px 10px",
                              border: "1px solid #bfdbfe",
                              borderRadius: "6px",
                              background: "#dbeafe",
                              color: "#1e40af",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "13px",
                            }}
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(r.report_id)}
                            style={{
                              padding: "6px 10px",
                              border: "1px solid #fecaca",
                              borderRadius: "6px",
                              background: "#fee2e2",
                              color: "#991b1b",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "13px",
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </div>
  );
}
